import { A } from '@ember/array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { all, restartableTask } from 'ember-concurrency';
import RouteTemplate from 'ember-route-template';
import set from 'ember-set-helper/helpers/set';
import EmberWormhole from 'ember-wormhole/components/ember-wormhole';
import NotificationPreferencesLoader from 'garaje/components/notification-preferences-loader';
import PropagableSettingsChanged from 'garaje/components/propagable-settings-changed';
import SetupGuideSteps from 'garaje/components/setup-guide-steps';
import TrialDaysLeft from 'garaje/components/trial-days-left';
import MessageBox from 'garaje/components/ui/classic/message-box';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import VisitorsNotificationsHostOptions from 'garaje/components/visitors/settings/notifications/host-options';
import preventDefault from 'garaje/helpers/prevent-default';
import type User from 'garaje/models/user';
import type CurrentAdminService from 'garaje/services/current-admin';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StatsigService from 'garaje/services/statsig';
import { and, equal, or, not } from 'macro-decorators';

import type VisitorsSettingsNotificationsController from './controller';

interface VisitorsSettingsNotificationsTemplateSignature {
  Args: {
    controller: VisitorsSettingsNotificationsController;
  };
}

class VisitorsSettingsNotificationsTemplate extends Component<VisitorsSettingsNotificationsTemplateSignature> {
  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: StoreService;
  @service declare statsig: StatsigService;

  @or('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin!: boolean;

  @not('args.controller.hasNotificationEnabled') hasNotificationDisabled!: boolean;
  @and('hasNotificationDisabled', 'args.controller.showBlankState') hasNoNotifications!: boolean;
  @equal('args.controller.employees.meta.total', 0) hasNoEmployees!: boolean;

  get showSetupGuide(): boolean {
    return this.isAdmin && this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper');
  }

  get locationIsConnectedToProperty(): boolean {
    return <number>this.args.controller.connectedTenants.length > 0;
  }

  get showDeviceStatusNotificationsSection(): boolean {
    return (
      this.args.controller.vrSubscription!.canEnableBadgePrinting &&
      (this.abilities.can('update printer notifications') || this.abilities.can('update device notifications'))
    );
  }

  searchUsersTask = restartableTask(async (term: string, selectedUsers: User[]) => {
    const userRoles = await this.store.query('user-role', {
      filter: { name: term, location: this.currentLocation.location.id },
    });
    let users = await all(userRoles.map((role) => role.user));
    const selectedUserIds = selectedUsers.map((contact) => contact.id);
    // deduplicate since roles could point to same user
    // exclude selected users
    users = A(users)
      .uniqBy('id')
      .filter((user) => {
        return !selectedUserIds.includes(user.id);
      });
    return users;
  });

  enable = async (): Promise<void> => {
    const location = this.currentLocation.location;
    location.hostNotificationsEnabled = true;
    await location.save();
    this.args.controller.showBlankState = false;
  };

  trackTooltipShown = () => {
    this.statsig.logEvent('device-notifications-moved-tooltip-seen');
  };

  trackTooltipLinkClick = () => {
    this.statsig.logEvent('device-notifications-moved-tooltip-link-clicked');
  };

  onUpdateFallbackContacts = ({ hasChanges }: { hasChanges: boolean }): void => {
    this.args.controller.fallbackContactsHasChanges = hasChanges;
  };

  <template>
    {{#if this.showSetupGuide}}
      <EmberWormhole @to="onboarding">
        <SetupGuideSteps />
      </EmberWormhole>
    {{/if}}

    <UiPage>
      <UiHeader>
        Notifications
        <TrialDaysLeft @subscription={{@controller.vrSubscription}} />
        <PropagableSettingsChanged />
      </UiHeader>

      {{#if this.hasNoEmployees}}
        <div class="pb-8 border-b min-w-0 min-h-0" data-test-empty-state>
          <div class="mx-auto text-center" style="max-width:600px;">
            <div class="empty-employees"></div>
            <h2 class="mt-8">Populate your employee directory before configuring notifications.</h2>
            <p class="my-4">You can sync employees from a directory service, import a CSV, or add them manually. Then,
              you’ll be able to automatically notify employees when their visitors arrive.</p>
            <LinkTo @route="employees.directory.add-employee.new" class="btn btn--small btn--brand mt-8">Add employees</LinkTo>

            <p class="text-sm block mt-8">
              Learn more about the
              <a href="https://help.envoy.com/employee-directory/" target="_blank" rel="noopener" tabindex="-1">employee
                directory</a>.
            </p>
          </div>
        </div>
      {{else if this.hasNoNotifications}}
        <div class="max-w-6xl mx-auto box-border w-full mt-16 mb-4" data-test-empty-state>
          <div class="flex">
            <img
              data-test-empty-notifications
              src="/assets/images/empty-state/notification-empty-state.svg"
              alt=""
              width="450"
              height="450"
              role="presentation"
            />
            <div class="w-6/12 ml-16 mt-4">
              <h2 class="mt-16">Send instant notifications</h2>
              <p class="text-base text-carbon-50 mt-4">Automatically notify your employees when their visitors arrive.
                It’s easy to send notifications via email, SMS, Slack and more.</p>
              <a
                href="#"
                class="btn btn--brand my-4"
                data-test-enabled-host-notifications
                {{on "click" (preventDefault this.enable)}}
              >Enable host notifications</a>
              <a
                class="text-base block mt-4 pt-4 border-t"
                href="https://help.envoy.com/notifications/"
                target="_blank"
                rel="noopener"
                tabindex="-1"
              >Learn more about host notifications →</a>
            </div>
          </div>
        </div>
      {{else}}
        <NotificationPreferencesLoader @location={{@controller.model}} as |pluginPreferences|>
          <VisitorsNotificationsHostOptions
            @location={{@controller.model}}
            @config={{@controller.config}}
            @vrSubscription={{@controller.vrSubscription}}
            @feature={{@controller.feature}}
            @reloadFallbackContacts={{@controller.reloadFallbackContacts}}
            @fallbackContacts={{@controller.fallbackContactsTask.lastSuccessful.value}}
            @onUpdateFallbackContacts={{this.onUpdateFallbackContacts}}
            @closeFeature={{fn (mut @controller.feature) ""}}
            @openFeature={{set @controller "feature"}}
            @pluginPreferences={{pluginPreferences}}
            @searchUsersTask={{this.searchUsersTask}}
            @locationIsConnectedToProperty={{this.locationIsConnectedToProperty}}
            @connectLocationConfiguration={{@controller.connectLocationConfiguration}}
          />
        </NotificationPreferencesLoader>
      {{/if}}

      {{#if this.showDeviceStatusNotificationsSection}}
        <MessageBox
          @type="attention"
          @header="Looking for device notifications?"
          class="my-2 w-[624px]"
          {{didInsert this.trackTooltipShown}}
          {{on "click" this.trackTooltipLinkClick}}
        >
          <p class="mb-0 mt-2">
            We've moved iPad and Printer status alerts to the
            <LinkTo data-test-link-to-email-templates @route="visitors.devices.alerts">
              Devices page
            </LinkTo>
          </p>
        </MessageBox>
      {{/if}}
    </UiPage>
  </template>
}

export default RouteTemplate(VisitorsSettingsNotificationsTemplate);
